<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;max-width:1300px;">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="st-page-title"><i class="mdi mdi-clipboard-check me-2"></i>Lesson Attendance</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <input class="st-input" type="date" v-model="selectedDate" @change="loadSchedule" style="width:150px;padding:7px 10px;" />
          <button class="btn st-btn-primary btn-sm" @click="loadSchedule">🔄</button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="la-tabs">
        <button class="la-tab" :class="{active: tab==='schedule'}" @click="tab='schedule'; loadSchedule()">📅 My Schedule</button>
        <button class="la-tab" :class="{active: tab==='reports'}" @click="tab='reports'">📊 Reports</button>
        <button class="la-tab" :class="{active: tab==='analytics'}" @click="tab='analytics'; loadAnalytics()">📈 Analytics</button>
      </div>

      <!-- ===== SCHEDULE TAB ===== -->
      <div v-if="tab==='schedule'">
        <!-- Staff check-in banner -->
        <div v-if="myAttendance" class="st-alert st-alert-success" style="margin-bottom:12px;">
          ✅ Checked in at {{ formatTime(myAttendance.check_in) }}
          <span v-if="myAttendance.status === 'late'" style="color:#F59E0B;"> ({{ myAttendance.late_minutes }} min late)</span>
        </div>
        <div v-else class="st-alert st-alert-danger" style="margin-bottom:12px;">
          ⚠️ You haven't checked in today. <button class="btn st-btn-primary btn-sm ms-2" style="padding:3px 12px;font-size:11px;" @click="selfCheckIn">Check In Now</button>
        </div>

        <!-- Progress -->
        <div v-if="schedule.length > 0" style="margin-bottom:16px;">
          <div class="d-flex justify-content-between mb-1" style="font-size:12px;color:#8BA4C4;">
            <span>Lessons marked: {{ markedCount }}/{{ schedule.length }}</span>
            <span>{{ selectedDate }}</span>
          </div>
          <div style="background:#1A3A6A;border-radius:6px;height:6px;overflow:hidden;">
            <div :style="{width: (markedCount/schedule.length*100)+'%', background:'#10B981', height:'100%', borderRadius:'6px', transition:'width .3s'}"></div>
          </div>
        </div>

        <div v-if="scheduleLoading" class="st-spinner"><span class="spinner-border"></span></div>
        <div v-else-if="schedule.length === 0" style="text-align:center;padding:40px;color:#4A6A8A;">
          <div style="font-size:40px;margin-bottom:8px;">📅</div>
          <p>No lessons scheduled for {{ selectedDate }}</p>
        </div>

        <!-- Lesson Cards -->
        <div v-for="slot in schedule" :key="slot.id" class="la-slot-card" :class="{'la-slot-marked': slot.attendance_marked_at}">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div class="la-slot-time">Period {{ slot.period_number }} · {{ slot.time_start?.substring(0,5) }} – {{ slot.time_end?.substring(0,5) }}</div>
              <div class="la-slot-subject">{{ slot.subject_name }}</div>
              <div class="la-slot-class">{{ slot.class_name }} <span v-if="slot.room" style="color:#4A6A8A;">· {{ slot.room }}</span></div>
            </div>
            <div style="text-align:right;">
              <div v-if="slot.attendance_marked_at" style="font-size:12px;color:#10B981;font-weight:600;">
                ✅ Marked · {{ slot.present_count }}P / {{ slot.absent_count }}A
              </div>
              <button class="btn st-btn-primary btn-sm mt-1" @click="openMarkModal(slot)">
                {{ slot.attendance_marked_at ? '✏️ Edit' : '📋 Mark Attendance' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== REPORTS TAB ===== -->
      <div v-if="tab==='reports'">
        <div class="st-section mb-3" style="padding:14px;">
          <div class="d-flex gap-2 flex-wrap align-items-end">
            <div>
              <label class="st-label" style="font-size:10px;">Class</label>
              <select class="st-input" v-model="rpt.classId" style="width:160px;padding:8px;" @change="loadReportSubjects">
                <option value="">Select class</option>
                <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div>
              <label class="st-label" style="font-size:10px;">Subject</label>
              <select class="st-input" v-model="rpt.subjectId" style="width:160px;padding:8px;">
                <option value="">Select subject</option>
                <option v-for="s in subjects" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div>
              <label class="st-label" style="font-size:10px;">Date</label>
              <input class="st-input" type="date" v-model="rpt.date" style="width:140px;" />
            </div>
            <button class="btn st-btn-primary btn-sm" @click="loadSingleReport" :disabled="reportLoading">🔍 View</button>
            <button class="btn st-btn-outline btn-sm" @click="loadRangeReport" :disabled="reportLoading">📊 Range Report</button>
          </div>
          <div class="d-flex gap-2 flex-wrap align-items-end mt-2">
            <div><label class="st-label" style="font-size:10px;">From</label><input class="st-input" type="date" v-model="rpt.dateFrom" style="width:140px;" /></div>
            <div><label class="st-label" style="font-size:10px;">To</label><input class="st-input" type="date" v-model="rpt.dateTo" style="width:140px;" /></div>
            <button class="btn st-btn-outline btn-sm" @click="exportReportCsv">📥 CSV</button>
            <button class="btn st-btn-outline btn-sm" @click="exportReportPdf">📄 PDF</button>
          </div>
        </div>

        <!-- Single day report -->
        <div v-if="singleReport" class="st-section" style="padding:14px;">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <div style="font-size:16px;font-weight:700;color:#E8EDF4;">{{ singleReport.slot.subject_name }} — {{ singleReport.slot.class_name }}</div>
              <div style="font-size:12px;color:#8BA4C4;">{{ singleReport.date }} · Teacher: {{ singleReport.marked_by }}</div>
            </div>
            <div class="d-flex gap-3" style="font-size:14px;font-weight:700;">
              <span style="color:#10B981;">✅ {{ singleReport.present_count }}</span>
              <span style="color:#EF4444;">❌ {{ singleReport.absent_count }}</span>
              <span v-if="singleReport.late_count" style="color:#F59E0B;">⏰ {{ singleReport.late_count }}</span>
              <span v-if="singleReport.unmarked_count" style="color:#4A6A8A;">— {{ singleReport.unmarked_count }}</span>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div style="font-size:12px;font-weight:600;color:#10B981;margin-bottom:6px;">PRESENT ({{ singleReport.present_count }})</div>
              <div v-for="s in singleReport.present" :key="s.id" class="la-student-row la-present">
                <span>{{ s.name }}</span><span style="color:#4A6A8A;">{{ s.admission_no }}</span>
              </div>
              <div v-if="singleReport.late?.length" style="font-size:12px;font-weight:600;color:#F59E0B;margin:8px 0 6px;">LATE ({{ singleReport.late_count }})</div>
              <div v-for="s in singleReport.late" :key="'l'+s.id" class="la-student-row la-late">
                <span>{{ s.name }}</span><span style="color:#4A6A8A;">{{ s.admission_no }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div style="font-size:12px;font-weight:600;color:#EF4444;margin-bottom:6px;">ABSENT ({{ singleReport.absent_count }})</div>
              <div v-for="s in singleReport.absent" :key="'a'+s.id" class="la-student-row la-absent">
                <span>{{ s.name }}</span><span style="color:#4A6A8A;">{{ s.admission_no }}</span>
              </div>
              <div v-if="singleReport.unmarked?.length" style="font-size:12px;font-weight:600;color:#4A6A8A;margin:8px 0 6px;">NOT MARKED ({{ singleReport.unmarked_count }})</div>
              <div v-for="s in singleReport.unmarked" :key="'u'+s.id" class="la-student-row">
                <span>{{ s.name }}</span><span style="color:#4A6A8A;">{{ s.admission_no }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Range report -->
        <div v-if="rangeReport" class="st-section" style="padding:14px;overflow-x:auto;">
          <div class="mb-3">
            <div style="font-size:16px;font-weight:700;color:#E8EDF4;">{{ rangeReport.slot.subject_name }} — {{ rangeReport.slot.class_name }}</div>
            <div style="font-size:12px;color:#8BA4C4;">{{ rangeReport.date_from }} to {{ rangeReport.date_to }} · {{ rangeReport.total_lessons }} lessons · Avg: {{ rangeReport.avg_attendance_rate }}%</div>
          </div>
          <table class="la-table">
            <thead>
            <tr><th>Student</th><th>Admission</th><th>Present</th><th>Absent</th><th>Late</th><th>Rate</th>
              <th v-for="d in rangeReport.dates?.slice(0,20)" :key="d" style="font-size:9px;writing-mode:vertical-rl;padding:4px 2px;">{{ d.substring(5) }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="s in rangeReport.students" :key="s.id" :style="{background: s.attendance_rate < 70 ? 'rgba(239,68,68,.05)' : ''}">
              <td>{{ s.name }}</td>
              <td>{{ s.admission_no }}</td>
              <td style="color:#10B981;font-weight:600;">{{ s.present }}</td>
              <td style="color:#EF4444;font-weight:600;">{{ s.absent }}</td>
              <td style="color:#F59E0B;">{{ s.late }}</td>
              <td><span class="la-rate" :class="rateClass(s.attendance_rate)">{{ s.attendance_rate || 0 }}%</span></td>
              <td v-for="d in rangeReport.dates?.slice(0,20)" :key="d+s.id" class="la-dot-cell">
                <span class="la-dot" :class="dotClass(rangeReport.matrix?.[s.id]?.[d])"></span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===== ANALYTICS TAB ===== -->
      <div v-if="tab==='analytics'">
        <div class="st-section mb-3" style="padding:14px;">
          <div class="d-flex gap-2 flex-wrap align-items-end">
            <div><label class="st-label" style="font-size:10px;">From</label><input class="st-input" type="date" v-model="ana.dateFrom" style="width:140px;" /></div>
            <div><label class="st-label" style="font-size:10px;">To</label><input class="st-input" type="date" v-model="ana.dateTo" style="width:140px;" /></div>
            <div>
              <label class="st-label" style="font-size:10px;">Class</label>
              <select class="st-input" v-model="ana.classId" style="width:140px;padding:8px;"><option value="">All</option><option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option></select>
            </div>
            <button class="btn st-btn-primary btn-sm" @click="loadAnalytics" :disabled="anaLoading">📈 Analyze</button>
          </div>
        </div>

        <div v-if="anaLoading" class="st-spinner"><span class="spinner-border"></span></div>
        <div v-else-if="analytics">
          <!-- Overall rate -->
          <div class="la-big-rate" :class="rateClass(analytics.overall_rate)">{{ analytics.overall_rate }}%</div>
          <div style="text-align:center;font-size:13px;color:#8BA4C4;margin-bottom:16px;">
            Overall attendance · {{ analytics.present }} present / {{ analytics.absent }} absent / {{ analytics.late }} late out of {{ analytics.total_records }} records
          </div>

          <div class="row g-3">
            <!-- By Subject -->
            <div class="col-md-6">
              <div class="st-section" style="padding:14px;">
                <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📚 By Subject</div>
                <div v-for="s in analytics.by_subject" :key="s.subject_name" class="d-flex justify-content-between py-1" style="border-bottom:1px solid #1A3A6A;font-size:12px;">
                  <span style="color:#E8EDF4;">{{ s.subject_name }}</span>
                  <span class="la-rate" :class="rateClass(s.attendance_rate)">{{ s.attendance_rate }}%</span>
                </div>
              </div>
            </div>
            <!-- By Class -->
            <div class="col-md-6">
              <div class="st-section" style="padding:14px;">
                <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">🏫 By Class</div>
                <div v-for="c in analytics.by_class" :key="c.class_name" class="d-flex justify-content-between py-1" style="border-bottom:1px solid #1A3A6A;font-size:12px;">
                  <span style="color:#E8EDF4;">{{ c.class_name }}</span>
                  <span class="la-rate" :class="rateClass(c.attendance_rate)">{{ c.attendance_rate }}%</span>
                </div>
              </div>
            </div>
            <!-- Top Absentees -->
            <div class="col-md-6">
              <div class="st-section" style="padding:14px;">
                <div style="font-size:13px;font-weight:600;color:#EF4444;margin-bottom:8px;">🚨 Top Absentees</div>
                <div v-for="s in analytics.top_absentees?.slice(0,10)" :key="s.id" class="d-flex justify-content-between py-1" style="border-bottom:1px solid #1A3A6A;font-size:12px;">
                  <span style="color:#E8EDF4;">{{ s.name }} <span style="color:#4A6A8A;">{{ s.class_name }}</span></span>
                  <span style="color:#EF4444;font-weight:600;">{{ s.absences }} ({{ s.absence_rate }}%)</span>
                </div>
              </div>
            </div>
            <!-- Teacher Compliance -->
            <div class="col-md-6">
              <div class="st-section" style="padding:14px;">
                <div style="font-size:13px;font-weight:600;color:#F59E0B;margin-bottom:8px;">👩‍🏫 Teacher Compliance</div>
                <div v-for="t in analytics.teacher_compliance" :key="t.teacher_name" class="d-flex justify-content-between py-1" style="border-bottom:1px solid #1A3A6A;font-size:12px;">
                  <span style="color:#E8EDF4;">{{ t.teacher_name }}</span>
                  <span class="la-rate" :class="rateClass(t.compliance_rate)">{{ t.compliance_rate || 0 }}%</span>
                </div>
              </div>
            </div>
            <!-- At Risk Students -->
            <div class="col-12" v-if="analytics.at_risk_students?.length">
              <div class="st-section" style="padding:14px;border-left:3px solid #EF4444;">
                <div style="font-size:13px;font-weight:600;color:#EF4444;margin-bottom:8px;">⚠️ At-Risk Students (>30% absence)</div>
                <div class="d-flex flex-wrap gap-2">
                  <div v-for="s in analytics.at_risk_students" :key="s.id" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;font-size:12px;">
                    <strong style="color:#E8EDF4;">{{ s.name }}</strong> <span style="color:#4A6A8A;">{{ s.class_name }}</span>
                    <span style="color:#EF4444;font-weight:600;margin-left:6px;">{{ s.absence_rate }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== MARK ATTENDANCE MODAL ===== -->
      <div v-if="markModal" class="la-overlay" @click.self="markModal=null">
        <div class="la-modal">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 style="color:#E8EDF4;margin:0;font-size:15px;">📋 {{ markModal.subject_name }} — {{ markModal.class_name }}</h5>
              <span style="font-size:12px;color:#8BA4C4;">Period {{ markModal.period_number }} · {{ selectedDate }}</span>
            </div>
            <button class="btn" style="color:#8BA4C4;font-size:18px;padding:0;" @click="markModal=null">✕</button>
          </div>

          <!-- Quick actions -->
          <div class="d-flex gap-2 mb-3">
            <button class="btn st-btn-outline btn-sm" style="font-size:11px;" @click="markAllAs('present')">✅ All Present</button>
            <button class="btn st-btn-outline btn-sm" style="font-size:11px;" @click="markAllAs('absent')">❌ All Absent</button>
            <span style="flex:1;"></span>
            <span style="font-size:12px;color:#8BA4C4;align-self:center;">{{ markStudents.length }} students</span>
          </div>

          <!-- Student list -->
          <div v-if="markLoading" class="st-spinner"><span class="spinner-border"></span></div>
          <div v-else style="max-height:50vh;overflow-y:auto;">
            <div v-for="s in markStudents" :key="s.id" class="la-mark-row">
              <div style="flex:1;">
                <strong style="color:#E8EDF4;">{{ s.name }}</strong>
                <span style="color:#4A6A8A;margin-left:6px;font-size:11px;">{{ s.admission_no }}</span>
                <span v-if="s.absences_this_subject > 2" style="color:#EF4444;margin-left:6px;font-size:10px;">{{ s.absences_this_subject }} absences</span>
              </div>
              <div class="la-mark-btns">
                <button class="la-mark-btn" :class="{active: s._status==='present', present: s._status==='present'}" @click="s._status='present'">✅</button>
                <button class="la-mark-btn" :class="{active: s._status==='late', late: s._status==='late'}" @click="s._status='late'">⏰</button>
                <button class="la-mark-btn" :class="{active: s._status==='absent', absent: s._status==='absent'}" @click="s._status='absent'">❌</button>
                <button class="la-mark-btn" :class="{active: s._status==='excused', excused: s._status==='excused'}" @click="s._status='excused'">🔖</button>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 justify-content-end mt-3">
            <button class="btn st-btn-outline btn-sm" @click="markModal=null">Cancel</button>
            <button class="btn st-btn-primary btn-sm" @click="saveAttendance" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save Attendance
            </button>
          </div>
          <div v-if="saveMsg" class="st-alert mt-2" :class="saveOk ? 'st-alert-success' : 'st-alert-danger'">{{ saveMsg }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'LessonAttendance',
  components: { Navigation },
  data() {
    const ld = (offset=0) => { const d = new Date(Date.now() + offset*86400000); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); };
    return {
      tab: 'schedule',
      selectedDate: ld(),
      schedule: [], scheduleLoading: false, myAttendance: null,
      // Mark modal
      markModal: null, markStudents: [], markLoading: false, saving: false, saveMsg: '', saveOk: false,
      // Reports
      classes: [], subjects: [], reportLoading: false,
      rpt: { classId: '', subjectId: '', date: ld(), dateFrom: ld(-30), dateTo: ld() },
      singleReport: null, rangeReport: null,
      // Analytics
      ana: { dateFrom: ld(-30), dateTo: ld(), classId: '' },
      analytics: null, anaLoading: false,
    };
  },
  computed: {
    markedCount() { return this.schedule.filter(s => s.attendance_marked_at).length; },
  },
  mounted() { this.loadSchedule(); this.loadClasses(); this.loadSubjects(); },
  methods: {
    formatTime(dt) { if (!dt) return ''; const p = dt.split(' ')[1]?.split(':'); if (!p) return ''; const h = +p[0]; return `${h%12||12}:${p[1]} ${h>=12?'PM':'AM'}`; },
    rateClass(r) { r = parseFloat(r) || 0; if (r >= 85) return 'rate-good'; if (r >= 70) return 'rate-ok'; return 'rate-bad'; },
    dotClass(status) { return { present: 'dot-present', late: 'dot-late', absent: 'dot-absent', excused: 'dot-excused' }[status] || 'dot-none'; },

    async loadSchedule() {
      this.scheduleLoading = true;
      try {
        const res = await executeJson({ function: 'getMyScheduleToday', date: this.selectedDate }, simitrack);
        if (res.data.success) {
          this.schedule = res.data.data?.slots || [];
          this.myAttendance = res.data.data?.my_attendance;
        }
      } catch(e) {}
      this.scheduleLoading = false;
    },
    async selfCheckIn() {
      try {
        const res = await executeJson({ function: 'staffCheckIn' }, simitrack);
        if (res.data.success) {
          await this.loadSchedule(); // reload first so banner updates
        } else {
          alert(res.data.message || 'Check-in failed');
        }
      } catch(e) { alert('Error'); }
    },
    async loadClasses() {
      try {
        const res = await executeJson({ function: 'getClasses' }, simitrack);
        if (res.data.success) this.classes = res.data.data || [];
      } catch(e) {}
    },
    async loadSubjects() {
      try {
        const res = await executeJson({ function: 'getSubjects' }, simitrack);
        if (res.data.success) this.subjects = res.data.data || [];
      } catch(e) {}
    },
    loadReportSubjects() { /* subjects already loaded globally */ },

    // Mark attendance
    async openMarkModal(slot) {
      this.markModal = slot; this.markStudents = []; this.markLoading = true; this.saveMsg = '';
      try {
        const res = await executeJson({ function: 'getLessonStudents', slotId: slot.id, date: this.selectedDate }, simitrack);
        if (res.data.success) {
          this.markStudents = (res.data.data?.students || []).map(s => ({
            ...s,
            _status: s.att_status || 'present',
          }));
        }
      } catch(e) {}
      this.markLoading = false;
    },
    markAllAs(status) { this.markStudents.forEach(s => s._status = status); },
    async saveAttendance() {
      this.saving = true; this.saveMsg = '';
      const records = this.markStudents.map(s => ({ studentId: s.id, status: s._status, notes: '' }));
      try {
        const res = await executeJson({ function: 'markLessonAttendance', slotId: this.markModal.id, date: this.selectedDate, records: JSON.stringify(records) }, simitrack);
        this.saveMsg = res.data.message; this.saveOk = res.data.success;
        if (res.data.success) {
          setTimeout(() => { this.markModal = null; this.loadSchedule(); }, 1200);
        }
      } catch(e) { this.saveMsg = 'Error saving'; this.saveOk = false; }
      this.saving = false;
    },

    // Reports
    async loadSingleReport() {
      if (!this.rpt.classId || !this.rpt.subjectId) { alert('Select class and subject'); return; }
      this.reportLoading = true; this.singleReport = null; this.rangeReport = null;
      try {
        const res = await executeJson({ function: 'getLessonAttendanceList', classId: this.rpt.classId, subjectId: this.rpt.subjectId, date: this.rpt.date }, simitrack);
        if (res.data.success) this.singleReport = res.data.data;
      } catch(e) {}
      this.reportLoading = false;
    },
    async loadRangeReport() {
      if (!this.rpt.classId || !this.rpt.subjectId) { alert('Select class and subject'); return; }
      this.reportLoading = true; this.singleReport = null; this.rangeReport = null;
      try {
        const res = await executeJson({ function: 'getLessonAttendanceRange', classId: this.rpt.classId, subjectId: this.rpt.subjectId, dateFrom: this.rpt.dateFrom, dateTo: this.rpt.dateTo }, simitrack);
        if (res.data.success) this.rangeReport = res.data.data;
      } catch(e) {}
      this.reportLoading = false;
    },

    // Analytics
    async loadAnalytics() {
      this.anaLoading = true; this.analytics = null;
      try {
        const res = await executeJson({ function: 'getLessonAttendanceAnalytics', dateFrom: this.ana.dateFrom, dateTo: this.ana.dateTo, classId: this.ana.classId }, simitrack);
        if (res.data.success) this.analytics = res.data.data;
      } catch(e) {}
      this.anaLoading = false;
    },

    // Exports
    exportReportCsv() {
      const data = this.rangeReport || this.singleReport;
      if (!data) { alert('Load a report first'); return; }
      const inst = data.institution || '';
      let csv = `"${inst} — Lesson Attendance Report"\n`;
      csv += `"${data.slot?.subject_name} — ${data.slot?.class_name}"\n`;
      if (data.date_from) csv += `"Period: ${data.date_from} to ${data.date_to}"\n\n`;
      else csv += `"Date: ${data.date}"\n\n`;

      if (this.rangeReport) {
        csv += 'Student,Admission,Total,Present,Absent,Late,Rate\n';
        (data.students||[]).forEach(s => { csv += `"${s.name}","${s.admission_no}","${s.total_lessons}","${s.present}","${s.absent}","${s.late}","${s.attendance_rate}%"\n`; });
      } else {
        csv += 'Student,Admission,Status\n';
        (data.all_students||[]).forEach(s => { csv += `"${s.name}","${s.admission_no}","${s.att_status||'unmarked'}"\n`; });
      }
      const blob = new Blob(['\uFEFF'+csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `Lesson_Attendance_${data.slot?.subject_name}_${data.slot?.class_name}.csv`; a.click();
    },
    exportReportPdf() {
      const data = this.rangeReport || this.singleReport;
      if (!data) { alert('Load a report first'); return; }
      const inst = data.institution || '';
      let html = `<!DOCTYPE html><html><head><title>Lesson Attendance</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;padding:15mm}@page{size:A4 landscape;margin:12mm}h1{font-size:18px;color:#0D2B5E}h2{font-size:14px;color:#2E86DE;margin:4px 0 12px}.meta{font-size:11px;color:#666;margin-bottom:12px}table{width:100%;border-collapse:collapse;margin-top:8px}th{background:#0D2B5E;color:#fff;padding:5px 8px;font-size:10px;text-align:left}td{padding:4px 8px;border-bottom:1px solid #e0e0e0;font-size:10px}tr:nth-child(even){background:#f9fbfd}.good{color:#059669}.ok{color:#d97706}.bad{color:#dc2626;font-weight:600}.footer{margin-top:16px;padding-top:8px;border-top:1px solid #ddd;font-size:9px;color:#999;display:flex;justify-content:space-between}</style></head><body>`;
      html += `<h1>${inst}</h1><h2>Lesson Attendance: ${data.slot?.subject_name} — ${data.slot?.class_name}</h2>`;
      html += `<div class="meta">Teacher: ${data.slot?.teacher_name || data.marked_by || ''} · `;
      if (data.date_from) html += `Period: ${data.date_from} to ${data.date_to} · ${data.total_lessons} lessons · Avg: ${data.avg_attendance_rate}%`;
      else html += `Date: ${data.date} · Present: ${data.present_count} · Absent: ${data.absent_count}`;
      html += `</div>`;

      if (this.rangeReport) {
        html += '<table><tr><th>Student</th><th>Admission</th><th>Total</th><th>Present</th><th>Absent</th><th>Late</th><th>Rate</th></tr>';
        (data.students||[]).forEach(s => {
          const cls = (s.attendance_rate||0) >= 85 ? 'good' : (s.attendance_rate||0) >= 70 ? 'ok' : 'bad';
          html += `<tr><td>${s.name}</td><td>${s.admission_no}</td><td>${s.total_lessons}</td><td>${s.present}</td><td>${s.absent}</td><td>${s.late}</td><td class="${cls}">${s.attendance_rate||0}%</td></tr>`;
        });
      } else {
        html += '<table><tr><th>Student</th><th>Admission</th><th>Status</th></tr>';
        (data.all_students||[]).forEach(s => {
          const st = s.att_status || 'unmarked';
          const cls = st === 'present' ? 'good' : st === 'absent' ? 'bad' : 'ok';
          html += `<tr><td>${s.name}</td><td>${s.admission_no}</td><td class="${cls}">${st}</td></tr>`;
        });
      }
      html += '</table>';
      html += `<div class="footer"><span>${inst} — Simi Trac</span><span>Generated ${new Date().toLocaleString()}</span></div></body></html>`;
      const w = window.open('', '_blank'); w.document.write(html); w.document.close();
      setTimeout(() => w.print(), 600);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;margin-bottom:16px;}
.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:12px;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}
.st-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);color:#EF4444;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}

.la-tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:2px solid #1A3A6A;}
.la-tab{background:none;border:none;padding:10px 16px;color:#4A6A8A;font-size:13px;font-weight:600;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;}.la-tab.active{color:#4FC3F7;border-bottom-color:#4FC3F7;}.la-tab:hover{color:#8BA4C4;}

.la-slot-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:16px;margin-bottom:10px;border-left:4px solid #F59E0B;transition:.2s;}
.la-slot-card.la-slot-marked{border-left-color:#10B981;}
.la-slot-time{font-size:11px;color:#4FC3F7;font-weight:600;}
.la-slot-subject{font-size:16px;font-weight:700;color:#E8EDF4;}
.la-slot-class{font-size:12px;color:#8BA4C4;}

.la-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:999;}
.la-modal{background:#0D2B5E;border-radius:14px;padding:24px;width:95%;max-width:600px;max-height:90vh;overflow-y:auto;border:1px solid #1A3A6A;}

.la-mark-row{display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid #1A3A6A;}
.la-mark-btns{display:flex;gap:4px;}
.la-mark-btn{width:36px;height:36px;border:1px solid #1A3A6A;border-radius:8px;background:#0A1628;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:.15s;}
.la-mark-btn.active.present{border-color:#10B981;background:rgba(16,185,129,.15);}
.la-mark-btn.active.late{border-color:#F59E0B;background:rgba(245,158,11,.15);}
.la-mark-btn.active.absent{border-color:#EF4444;background:rgba(239,68,68,.15);}
.la-mark-btn.active.excused{border-color:#8B5CF6;background:rgba(139,92,246,.15);}

.la-student-row{display:flex;justify-content:space-between;padding:5px 10px;border-radius:6px;margin-bottom:3px;font-size:12px;}
.la-present{background:rgba(16,185,129,.06);color:#E8EDF4;}
.la-late{background:rgba(245,158,11,.06);color:#E8EDF4;}
.la-absent{background:rgba(239,68,68,.06);color:#E8EDF4;}

.la-table{width:100%;border-collapse:collapse;font-size:11px;color:#E8EDF4;}
.la-table th{background:#0A1628;padding:6px 8px;color:#4FC3F7;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;white-space:nowrap;}
.la-table td{padding:5px 8px;border-bottom:1px solid #1A3A6A;}

.la-rate{font-weight:700;font-size:12px;padding:2px 6px;border-radius:4px;}
.rate-good{color:#10B981;background:rgba(16,185,129,.1);}
.rate-ok{color:#F59E0B;background:rgba(245,158,11,.1);}
.rate-bad{color:#EF4444;background:rgba(239,68,68,.1);}

.la-dot-cell{text-align:center;padding:2px!important;}
.la-dot{display:inline-block;width:10px;height:10px;border-radius:50%;}
.dot-present{background:#10B981;}
.dot-late{background:#F59E0B;}
.dot-absent{background:#EF4444;}
.dot-excused{background:#8B5CF6;}
.dot-none{background:#1A3A6A;}

.la-big-rate{text-align:center;font-size:56px;font-weight:800;margin:10px 0 4px;}
</style>